import { useEffect, useState } from "react";
import * as S from "./style";
import LayoutDetails from "../../../LayoutModal/LayoutDetails";
import axiosInstance from "services/axiosInstance";
import Loading from "../../../Loading";
import PayInvoice from "../Update";
/**
 * Icons
 */
import { Circle, CaretRight, CheckCircle } from "@phosphor-icons/react";
/**
 * hooks
 */
import useLoading from "../../../../hooks/useLoading";
import { useMultipleModal } from "../../../../hooks/useMultipleModal";
/**
 * utils
 */
import FormatarData from "utils/FormatData";
import { CurrencyFormat } from "utils/CurrencyFormat";
/**
 * Interface
 */
import { IPaymentCredits } from "Interfaces/IPayCredit";

interface IDetails {
  isOpenModalDetails: boolean;
  setModalClose: () => void;
  selectedUrlId: number | null;
  customerName: string;
}

export default function Invoices({
  isOpenModalDetails,
  setModalClose,
  selectedUrlId,
  customerName,
}: IDetails) {
  const [data, setData] = useState<IPaymentCredits | null>(null);
  const { isLoading, startLoading, stopLoading } = useLoading();
  const { isOpenModal, toggleModalVisibility } = useMultipleModal();
  const [installmentId, setInstallmentId] = useState<number | null>(null);
  const [installmentValue, setInstallmentValue] = useState("");
  const [idCred, setIdCredit] = useState<number | null>(null);

  const fetchData = async () => {
    startLoading();
    try {
      if (selectedUrlId !== null) {
        const response = await axiosInstance.get(
          `payment-credit/${selectedUrlId}`
        );
        setData(response.data);
      } else {
        console.log("Dado não encontrado!");
      }
    } catch (error) {
      console.log("Error: ", error);
    } finally {
      stopLoading();
    }
  };

  useEffect(() => {
    if (selectedUrlId !== null) {
      fetchData();
    }
  }, [selectedUrlId]);

  const clickInvoices = (id: number, value: string, idCredit: number) => {
    setInstallmentId(id);
    setInstallmentValue(value);
    setIdCredit(idCredit);
    toggleModalVisibility("payment");
  };

  /**
   *
   * @param invoices
   * @returns {number}
   * Obtem o total do valor pendente
   */
  const getPendingAmount = (invoices: IPaymentCredits) => {
    // Converter total da venda para número
    const totalSale = parseFloat(invoices.total_amount);

    // Somar os valores das parcelas pagas
    const totalPaid = invoices.installments
      .filter((installment) => installment.status === "Pago")
      .reduce((acc, installment) => acc + parseFloat(installment.amount), 0);

    // Obter a primeira parcela pendente
    const firstPendingInstallment = invoices.installments.find(
      (installment) => installment.status === "Pendente"
    );

    const validity =
      FormatarData(firstPendingInstallment?.due_date || "-", "day-month") ||
      null;

    const currentInstallValue = CurrencyFormat(
      parseFloat(firstPendingInstallment?.amount || "0")
    );

    // Calcular o saldo pendente
    const pendingAmount = totalSale - totalPaid;

    // Retornar o valor formatado
    return {
      pending: pendingAmount,
      paid: totalPaid,
      validity,
      currentInstallValue,
    };
  };

  if (isOpenModalDetails && data) {
    return (
      <LayoutDetails
        title="Resumo de faturas"
        subTitle="Veja o resumo completo das faturas do cliente."
        setModalClose={setModalClose}
      >
        {isLoading ? (
          <Loading />
        ) : (
          <S.Content>
            <S.Header>
              <S.ContentInfor>
                <S.Infor>
                  <p>Fatura atual</p>
                </S.Infor>
                <S.Title>{getPendingAmount(data).currentInstallValue}</S.Title>
                {getPendingAmount(data).validity ? (
                  <S.Infor>
                    <span>Vencimento:</span>
                    <span>{getPendingAmount(data).validity}</span>
                  </S.Infor>
                ) : (
                  <S.Confirme>
                    <CheckCircle size={16} />
                    <p>Quitado</p>
                  </S.Confirme>
                )}

                <S.Infor>
                  <p>Total Pago:</p>
                  <p>{CurrencyFormat(getPendingAmount(data).paid)}</p>
                </S.Infor>

                <S.Infor>
                  <p>Cliente:</p>
                  <p>{customerName}</p>
                </S.Infor>
              </S.ContentInfor>
            </S.Header>

            <S.Body>
              {data.installments.map((val) => {
                const isPaid = val.status === "Pago";

                return (
                  <S.SectionButton
                    isPaid={isPaid}
                    onClick={() =>
                      !isPaid &&
                      clickInvoices(val.id, val.amount, val.payment_credit_id)
                    }
                    disabled={isPaid}
                  >
                    <S.ContentInfor>
                      <S.TitleButton>
                        <p>
                          {FormatarData(
                            val.due_date || "-",
                            "only-month"
                          ).toUpperCase()}
                        </p>
                      </S.TitleButton>

                      <S.Infor>
                        <p>{FormatarData(val.due_date || "-", "default")}</p>
                      </S.Infor>
                      {isPaid ? (
                        <S.Status isPay={true}>
                          <Circle size={8} weight="fill" />
                          <p>{val.status}</p>
                        </S.Status>
                      ) : (
                        <S.Status isPay={false}>
                          <Circle size={8} weight="fill" />
                          <p>{val.status}</p>
                        </S.Status>
                      )}
                    </S.ContentInfor>

                    <CaretRight size={16} weight="bold" />
                  </S.SectionButton>
                );
              })}
            </S.Body>
          </S.Content>
        )}

        <PayInvoice
          selectedId={installmentId}
          total={installmentValue}
          selectedIdCredit={idCred}
          isOpenModalCustomer={isOpenModal("payment")}
          setModalClose={() => toggleModalVisibility("payment")}
          refreshFrames={fetchData}
        />
      </LayoutDetails>
    );
  }
  return null;
}
